// src/hooks/useNotifications.js
import { useEffect, useState, useCallback, useRef } from 'react';
import { onMessage, getToken } from 'firebase/messaging';
import { messaging } from '../services/firebase';
import { registerFCMToken } from '../services/userService';
import { isPWA } from '../utils/deviceCheck';
import {
    SERVICE_WORKER_SCOPE,
    SERVICE_WORKER_URL,
} from '../constants/serviceWorker';

const IS_APP_BADGE_ENABLED = false; //Disable app badge behavior until Product specs it out

const useNotifications = () => {
    const [isPushSupported, setIsPushSupported] = useState(true);
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);
    const [badgeCount, setBadgeCount] = useState(0); //State to track badge count

    const syncInFlightRef = useRef(false); //Ref to track if syncFCMRegistration is in progress

    //Check for Push and Notification support
    useEffect(() => {
        if (!('PushManager' in window) || !('Notification' in window)) {
            setIsPushSupported(false);
            console.warn(
                'Push notifications are not supported in this browser.',
            );
        }
    }, []);

    useEffect(() => {
        //Register the firebase service worker
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker
                .register(SERVICE_WORKER_URL, { scope: SERVICE_WORKER_SCOPE })
                .then(registration => {
                    console.log(
                        'Service Worker registered with scope:',
                        registration.scope,
                    );
                    console.log('[sw-check]', {
                        controller:
                            navigator.serviceWorker.controller?.scriptURL ??
                            'none',
                        active: registration.active?.scriptURL ?? 'none',
                        waiting: registration.waiting?.scriptURL ?? 'none',
                        installing:
                            registration.installing?.scriptURL ?? 'none',
                    });
                })
                .catch(err => {
                    console.error('Service Worker registration failed:', err);
                });
        }

        if (!isPushSupported || !messaging) {
            return undefined;
        }

        const unsubscribe = onMessage(messaging, async payload => {
            console.log('Foreground message received ', payload);
            const notificationTitle =
                payload.notification?.title ??
                payload.data?.title ??
                'Notification';

            const notificationOptions = {
                body: payload.notification?.body ?? payload.data?.body,
                icon: payload.notification?.icon ?? payload.data?.icon,
                // Carries the destination so the service worker can route the tap.
                data: payload.data ?? {},
            };

            //Check browser's permission
            if (Notification.permission !== 'granted') {
                console.log('Notification permission not granted.');
                return;
            }

            try {
                //Display notification while app is in foreground
                const registration = await navigator.serviceWorker.ready;
                await registration.showNotification(
                    notificationTitle,
                    notificationOptions,
                );

                //Increment badge count
                setBadgeCount(prevCount => {
                    const newCount = prevCount + 1;
                    if (IS_APP_BADGE_ENABLED && 'setAppBadge' in navigator) {
                        navigator.setAppBadge(newCount).catch(err => {
                            console.error('Error setting badge:', err);
                        });
                    }
                    return newCount;
                });
            } catch (err) {
                console.error('Failed to show notification:', err);
            }
        });

        //Unsubscribe from onMessage when component unmounts
        return () => {
            unsubscribe();
        };
    }, [isPushSupported, messaging]);

    //Clear the badge when the app is launched
    useEffect(() => {
        if ('clearAppBadge' in navigator) {
            navigator
                .clearAppBadge()
                .then(() => {
                    setBadgeCount(0); // Reset badge count in state
                })
                .catch(error => {
                    console.error('Error clearing badge:', error);
                });
        }
    }, []);

    const syncFCMRegistration = useCallback(async () => {
        if (
            !isPushSupported ||
            !messaging ||
            !('serviceWorker' in navigator) ||
            !('Notification' in window)
        ) {
            return null;
        }

        //Silently repair registration if permission has already been granted
        if (Notification.permission !== 'granted') {
            return null;
        }

        //Prevent multiple simultaneous syncs
        if (syncInFlightRef.current) {
            return syncInFlightRef.current;
        }

        const syncOperation = (async () => {
            await navigator.serviceWorker.register(SERVICE_WORKER_URL, {
                scope: SERVICE_WORKER_SCOPE,
            });

            const serviceWorkerRegistration =
                await navigator.serviceWorker.ready;

            const currentToken = await getToken(messaging, {
                vapidKey: process.env.REACT_APP_VAPID_KEY,
                serviceWorkerRegistration,
            });

            if (!currentToken) {
                throw new Error('Firebase did not return FCM token.');
            }

            //Create device id if it's not present and send token and device id if registration needed
            await registerFCMToken(currentToken);

            setNotificationsEnabled(true);
            return currentToken;
        })();

        syncInFlightRef.current = syncOperation;

        try {
            return await syncOperation;
        } finally {
            if (syncInFlightRef.current === syncOperation) {
                syncInFlightRef.current = null;
            }
        }
    }, [isPushSupported, messaging]);

    const requestNotificationPermission = useCallback(async () => {
        if (!isPushSupported || !('Notification' in window)) {
            throw new Error(
                'Push notifications are not supported in this browser.',
            );
        }

        const installedPWA = await isPWA();

        if (!installedPWA) {
            throw new Error(
                'Push notifications are only available in the installed app.',
            );
        }

        if (Notification.permission === 'denied') {
            throw new Error(
                'Notification permission is blocked. Please enable notifications in your browser settings.',
            );
        }

        let permission = Notification.permission;

        if (permission === 'default') {
            permission = await Notification.requestPermission();
        }

        if (permission !== 'granted') {
            setNotificationsEnabled(false);
            throw new Error('Notification permission was not granted.');
        }

        const currentToken = await syncFCMRegistration();

        if (!currentToken) {
            throw new Error('Firebase did not return FCM token.');
        }

        return currentToken;
    }, [isPushSupported, syncFCMRegistration]);

    return {
        isPushSupported,
        notificationsEnabled,
        requestNotificationPermission,
        syncFCMRegistration,
    };
};

export default useNotifications;
